/**
 * @module org/locationtech/jts/io/WKTReader
 */

import * as geom from 'jsts/org/locationtech/jts/geom';
import WKTParser from './WKTParser';

const PrecisionModel = geom.PrecisionModel;
const GeometryFactory = geom.GeometryFactory;

/**
 * Converts a geometry in Well-Known Text format to a {@link Geometry}.
 * <p>
 * <code>WKTReader</code> supports extracting <code>Geometry</code> objects
 * from either {@link Reader}s or {@link String}s. This allows it to function
 * as a parser to read <code>Geometry</code> objects from text blocks embedded
 * in other data formats (e.g. XML).
 */
export default class WKTReader {
    /**
     * A <code>WKTReader</code> is parameterized by a <code>GeometryFactory</code>,
     * to allow it to create <code>Geometry</code> objects of the appropriate
     * implementation. In particular, the <code>GeometryFactory</code> determines
     * the <code>PrecisionModel</code> and <code>SRID</code> that is used.
     * @param {GeometryFactory} geometryFactory
     * @constructor
     */
    constructor(geometryFactory) {
        this.geometryFactory = geometryFactory || new GeometryFactory();
        this.precisionModel = this.geometryFactory.getPrecisionModel();
        this.parser = new WKTParser(this.geometryFactory);
    }

    /**
     * Reads a Well-Known Text representation of a {@link Geometry}
     *
     * @param {string}
     *          wkt a <Geometry Tagged Text> string (see the OpenGIS Simple Features
     *          Specification).
     * @return {Geometry} a <code>Geometry</code> read from
     *         <code>string.</code>
     * @memberof module:org/locationtech/jts/io/WKTReader#
     */
    read(wkt) {
        const geometry = this.parser.read(wkt);

        // TODO: port and use GeometryPrecisionReducer, this is a hack
        if (this.precisionModel.getType() === PrecisionModel.FIXED) {
            this.reducePrecision(geometry);
        }

        return geometry;
    }

    reducePrecision(geometry) {
        const coordinates = geometry.getCoordinates();
        for (let i = 0; i < coordinates.length; i++) {
            this.precisionModel.makePrecise(coordinates[i]);
        }
    }
}
